<template>
  <div class="container-fluid social-posts-page">
    <div class="page-header">
      <div>
        <h3 class="h3 my-0 mx-0 text-foreground">Social Media Posts</h3>
        <p class="text-muted-foreground text-sm">Generate and manage AI-powered social media content</p>
      </div>
      <div class="flex items-center space-x-4">
        <router-link
          v-if="user && user.business"
          :to="{
            name: 'request-reviews',
            params: {
              /* eslint-disable-next-line vue/camelcase */
              unique_id: user.business.unique_id + '-' + user.business.id,
            },
          }"
          class="btn bg-primary space-x-3 round-xl px-4 request-review-btn"
          target="_blank"
          ><i class="uil uil-star"></i
          ><span class="text-sm font-medium"> Request Review</span>
        </router-link>
      </div>
    </div>
    <hr class="page-divider" />
    <!-- Main Content -->
        <!-- Tabs -->
        <div class="tabs-container">
          <div class="tabs-list" role="tablist">
            <button
              type="button"
              role="tab"
              :aria-selected="activeTab === 'posts'"
              class="tab-button"
              :class="{ active: activeTab === 'posts' }"
              @click="activeTab = 'posts'"
            >
              Social Posts
            </button>
            <button
              type="button"
              role="tab"
              :aria-selected="activeTab === 'settings'"
              class="tab-button"
              :class="{ active: activeTab === 'settings' }"
              @click="activeTab = 'settings'"
            >
              Social Posts Settings
            </button>
          </div>
        </div>

        <!-- Tab Panels -->
        <div class="tab-panels">
          <!-- Social Posts Tab -->
          <div
            v-show="activeTab === 'posts'"
            role="tabpanel"
            class="tab-panel"
          >
            <div class="space-y-8">
              <!-- Generate New Post Card -->
              <div class="card">
                <div class="card-header">
                  <h3 class="card-title">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="title-icon">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                    Generate New Post
                  </h3>
                  <p class="card-description">Create AI-generated social media content for your platforms</p>
                </div>
                <div class="card-body">
                  <div class="form-grid">
                    <div class="form-col-1">
                      <label class="form-label" for="postType">Choose A Post Type</label>
                      <b-form-select
                        id="postType"
                        v-model="selectedCategory"
                        class="post-type-select"
                      >
                        <option value="Famous Quotes">Famous Quotes</option>
                        <option value="Educational Content">Educational Content</option>
                        <option value="Seasonal-Holiday Posts">Seasonal/Holiday Posts</option>
                        <option value="Engagement Posts">Engagement Posts</option>
                        <option value="Fun Facts">Fun Facts</option>
                        <option value="Humorous Posts">Humorous Posts</option>
                        <option value="Quote of the Day">Quote of the Day</option>
                        <option value="Interactive Content">Interactive Content</option>
                      </b-form-select>
                    </div>
                    <div class="form-col-2">
                      <label class="form-label" for="instructions">Additional Instructions (not required)</label>
                      <textarea
                        id="instructions"
                        v-model="additionalInfo"
                        class="instructions-textarea"
                        placeholder="Provide additional information to further refine the post."
                      ></textarea>
                      <div class="button-wrapper">
                        <button
                          class="generate-btn"
                          :disabled="loading"
                          @click="generatePost"
                        >
                          <b-spinner v-if="loading" small class="mr-2"></b-spinner>
                          Generate Post
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Recent Posts Card -->
              <div class="card">
                <div class="card-header">
                  <h3 class="card-title-muted">Recent Posts</h3>
                </div>
                <div class="card-body">
                  <div v-if="posts.length === 0" class="empty-posts">
                    <p class="empty-posts-text">No posts yet. Generate your first post above!</p>
                  </div>
                  <div v-else class="posts-list">
                    <div
                      v-for="post in posts"
                      :key="post.id || post.date"
                      class="post-item"
                    >
                      <div class="post-grid">
                        <div class="post-image-col">
                          <img
                            :src="post.imageUrl || '/api/placeholder/400/300'"
                            alt="Generated post"
                            class="post-image"
                          />
                        </div>
                        <div class="post-content-col">
                          <div class="post-text-content">
                            <!-- eslint-disable-next-line vue/no-v-html -->
                            <p class="post-text" v-html="post.text"></p>
                            <!-- eslint-disable-next-line vue/no-v-html -->
                            <p class="post-hashtags" v-html="post.hashtags"></p>
                          </div>
                          <div class="post-buttons">
                            <button
                              v-if="user && user.business && user.business.facebook_token && user.business.facebook_token.fb_page_id"
                              class="post-social-btn"
                              :class="{
                                'btn-facebook': true,
                                'btn-posted': post.facebook_post,
                              }"
                              :disabled="post.working"
                              @click="confirmPostSocial(post, 'facebook')"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="btn-icon">
                                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                              </svg>
                              <span v-if="post.facebook_post">Posted</span>
                              <span v-else>Post</span> to Facebook
                            </button>
                            <button
                              v-if="user && user.business && user.business.facebook_token && user.business.facebook_token.ig_identifier"
                              class="post-social-btn"
                              :class="{
                                'btn-instagram': true,
                                'btn-posted': post.instagram_post,
                              }"
                              :disabled="post.working"
                              @click="confirmPostSocial(post, 'instagram')"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="btn-icon">
                                <rect width="20" height="20" x="2" y="2" rx="5" ry="5"></rect>
                                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                                <line x1="17.5" x2="17.51" y1="6.5" y2="6.5"></line>
                              </svg>
                              <span v-if="post.instagram_post">Posted</span>
                              <span v-else>Post</span> to Instagram
                            </button>
                            <button
                              v-if="user && user.business && user.business.location_id"
                              class="post-social-btn"
                              :class="{
                                'btn-google': true,
                                'btn-posted': post.gbp_post,
                              }"
                              :disabled="post.working"
                              @click="confirmPostSocial(post, 'google')"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" class="btn-icon">
                                <rect width="24" height="24" rx="5" ry="5" fill="#fff"></rect>
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"></path>
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"></path>
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"></path>
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"></path>
                              </svg>
                              <span v-if="post.gbp_post">Posted</span>
                              <span v-else>Post</span> to Google
                            </button>
                          </div>
                          <div v-if="post.facebook_post || post.instagram_post || post.gbp_post" class="post-dates">
                            <span v-if="post.facebook_post" class="post-date">{{ formattedDate(post) }}</span>
                            <span v-if="post.instagram_post" class="post-date">{{ formattedInstagramDate(post) }}</span>
                            <span v-if="post.gbp_post" class="post-date">{{ formattedInstagramDate(post) }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-if="hasMorePosts && posts.length > 0" class="load-more-wrapper">
                    <button class="load-more-btn" @click="loadMore">
                      Load More
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Social Posts Settings Tab -->
          <div
            v-show="activeTab === 'settings'"
            role="tabpanel"
            class="tab-panel"
          >
            <div class="space-y-8">
              <!-- Auto-Sharing Settings Card -->
              <div class="card">
                <div class="card-header">
                  <h3 class="card-title">Auto-Sharing Settings</h3>
                  <p class="card-description">Configure automatic posting and approval settings for social media platforms</p>
                </div>
                <div class="card-body">
                  <div class="settings-list">
                    <!-- Email Approval Toggle -->
                    <div class="setting-item">
                      <div class="setting-label-group">
                        <label class="setting-label">Send Email For Post Approval on Facebook, Instagram, and Google</label>
                        <p class="setting-hint">Require email approval before posts are published.</p>
                      </div>
                      <b-form-checkbox
                        v-model="settings.emailApproval"
                        switch
                        size="sm"
                        class="feedback-switch-style"
                      />
                    </div>

                    <!-- Facebook Auto-Sharing Toggle -->
                    <div class="setting-item">
                      <div class="setting-label-group">
                        <div class="setting-label-with-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" class="platform-icon facebook-icon">
                            <rect width="24" height="24" rx="5" ry="5" fill="#1877F2"></rect>
                            <path d="M16.671 15.543l.532-3.47h-3.328v-2.25c0-.949.465-1.874 1.956-1.874h1.514V5.096s-1.374-.235-2.686-.235c-2.741 0-4.533 1.662-4.533 4.669v2.66H7.078v3.47h3.047v8.385c.61.096 1.235.146 1.875.146.64 0 1.265-.05 1.875-.146v-8.385h2.796z" fill="#fff"></path>
                          </svg>
                          <label class="setting-label">Enable Auto-Sharing on Facebook</label>
                        </div>
                      </div>
                      <b-form-checkbox
                        v-model="settings.facebookAutoShare"
                        switch
                        size="sm"
                        class="feedback-switch-style"
                      />
                    </div>

                    <!-- Instagram Auto-Sharing Toggle -->
                    <div class="setting-item">
                      <div class="setting-label-group">
                        <div class="setting-label-with-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" class="platform-icon instagram-icon">
                            <defs>
                              <linearGradient :id="'igIconSettings' + _uid" x1="0%" y1="100%" x2="100%" y2="0%">
                                <stop offset="0%" stop-color="#F58529"></stop>
                                <stop offset="25%" stop-color="#FEDA77"></stop>
                                <stop offset="50%" stop-color="#DD2A7B"></stop>
                                <stop offset="75%" stop-color="#8134AF"></stop>
                                <stop offset="100%" stop-color="#515BD4"></stop>
                              </linearGradient>
                            </defs>
                            <rect width="24" height="24" rx="5.5" ry="5.5" :fill="'url(#igIconSettings' + _uid + ')'"></rect>
                            <path d="M12 2.982c2.937 0 3.285.011 4.445.064a6.087 6.087 0 0 1 2.042.379 3.408 3.408 0 0 1 1.265.823 3.408 3.408 0 0 1 .823 1.265 6.087 6.087 0 0 1 .379 2.042c.053 1.16.064 1.508.064 4.445s-.011 3.285-.064 4.445a6.087 6.087 0 0 1-.379 2.042 3.643 3.643 0 0 1-2.088 2.088 6.087 6.087 0 0 1-2.042.379c-1.16.053-1.508.064-4.445.064s-3.285-.011-4.445-.064a6.087 6.087 0 0 1-2.042-.379 3.408 3.408 0 0 1-1.265-.823 3.408 3.408 0 0 1-.823-1.265 6.087 6.087 0 0 1-.379-2.042C2.993 15.285 2.982 14.937 2.982 12s.011-3.285.064-4.445a6.087 6.087 0 0 1 .379-2.042 3.408 3.408 0 0 1 .823-1.265 3.408 3.408 0 0 1 1.265-.823 6.087 6.087 0 0 1 2.042-.379c1.16-.053 1.508-.064 4.445-.064M12 1c-2.987 0-3.362.013-4.535.066a8.074 8.074 0 0 0-2.67.511 5.392 5.392 0 0 0-1.949 1.27 5.392 5.392 0 0 0-1.27 1.949 8.074 8.074 0 0 0-.511 2.67C1.013 8.638 1 9.013 1 12s.013 3.362.066 4.535a8.074 8.074 0 0 0 .511 2.67 5.392 5.392 0 0 0 1.27 1.949 5.392 5.392 0 0 0 1.949 1.27 8.074 8.074 0 0 0 2.67.511C8.638 22.987 9.013 23 12 23s3.362-.013 4.535-.066a8.074 8.074 0 0 0 2.67-.511 5.625 5.625 0 0 0 3.219-3.219 8.074 8.074 0 0 0 .511-2.67C22.987 15.362 23 14.987 23 12s-.013-3.362-.066-4.535a8.074 8.074 0 0 0-.511-2.67 5.392 5.392 0 0 0-1.27-1.949 5.392 5.392 0 0 0-1.949-1.27 8.074 8.074 0 0 0-2.67-.511C15.362 1.013 14.987 1 12 1z" fill="white"></path>
                            <path d="M12 6.351A5.649 5.649 0 1 0 17.649 12 5.649 5.649 0 0 0 12 6.351zm0 9.316A3.667 3.667 0 1 1 15.667 12 3.667 3.667 0 0 1 12 15.667z" fill="white"></path>
                            <circle cx="17.872" cy="6.128" r="1.32" fill="white"></circle>
                          </svg>
                          <label class="setting-label">Enable Auto-Sharing on Instagram</label>
                        </div>
                      </div>
                      <b-form-checkbox
                        v-model="settings.instagramAutoShare"
                        switch
                        size="sm"
                        class="feedback-switch-style"
                      />
                    </div>

                    <!-- Google Auto-Sharing Toggle -->
                    <div class="setting-item">
                      <div class="setting-label-group">
                        <div class="setting-label-with-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" class="platform-icon google-icon">
                            <rect width="24" height="24" rx="5" ry="5" fill="#fff"></rect>
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"></path>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"></path>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"></path>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"></path>
                          </svg>
                          <label class="setting-label">Enable Auto-Sharing on Google Business Profile</label>
                        </div>
                      </div>
                      <b-form-checkbox
                        v-model="settings.googleAutoShare"
                        switch
                        size="sm"
                        class="feedback-switch-style"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Content & Scheduling Settings Card -->
              <div class="card">
                <div class="card-header">
                  <h3 class="card-title">Content & Scheduling Settings</h3>
                  <p class="card-description">Configure post types and scheduling preferences</p>
                </div>
                <div class="card-body">
                  <!-- Social Media Post Types -->
                  <div class="settings-field">
                    <label class="field-label">Social Media Post Types:</label>
                    <div class="post-types-tags">
                      <button
                        v-for="postType in postTypes"
                        :key="postType"
                        type="button"
                        class="post-type-tag"
                        :class="{ active: settings.selectedPostTypes.includes(postType) }"
                        @click="togglePostType(postType)"
                      >
                        {{ postType }}
                        <span v-if="settings.selectedPostTypes.includes(postType)" class="tag-remove">×</span>
                      </button>
                    </div>
                  </div>

                  <!-- Additional Instructions -->
                  <div class="settings-field">
                    <label class="field-label">Additional Instructions:</label>
                    <p class="field-hint">Provide additional information to further refine the post.</p>
                    <textarea
                      v-model="settings.additionalInstructions"
                      class="settings-textarea"
                      placeholder="Provide additional information to further refine the post."
                    ></textarea>
                  </div>

                  <!-- Generate Social Media Posts -->
                  <div class="settings-field">
                    <label class="field-label">Generate Social Media Posts:</label>
                    
                    <!-- Days of the week -->
                    <div class="days-selection">
                      <button
                        v-for="day in daysOfWeek"
                        :key="day.value"
                        type="button"
                        class="day-button"
                        :class="{ active: settings.selectedDays.includes(day.value) }"
                        @click="toggleDay(day.value)"
                      >
                        {{ day.label }}
                      </button>
                    </div>

                    <!-- Time range, Limit, and Timezone -->
                    <div class="scheduling-controls-wrapper">
                      <!-- Time range -->
                      <div class="time-range-group">
                        <span class="time-range-label">Between</span>
                        <b-form-select
                          v-model="settings.startTime"
                          :options="timeOptions"
                          class="time-select"
                        ></b-form-select>
                        <span class="time-range-label">and</span>
                        <b-form-select
                          v-model="settings.endTime"
                          :options="timeOptions"
                          class="time-select"
                        ></b-form-select>
                      </div>

                      <!-- Post limit -->
                      <div class="limit-group">
                        <span class="limit-label">Limit to</span>
                        <b-form-select
                          v-model="settings.postLimit"
                          :options="limitOptions"
                          class="limit-select"
                        ></b-form-select>
                        <span class="limit-label">social media posts per day.</span>
                      </div>

                      <!-- Timezone -->
                      <div class="timezone-group">
                        <span class="timezone-label">Timezone:</span>
                        <b-form-select
                          v-model="settings.timezone"
                          :options="timezoneOptions"
                          class="timezone-select"
                        ></b-form-select>
                      </div>
                    </div>
                  </div>

                  <!-- Save Button -->
                  <div class="settings-footer">
                    <button
                      class="save-settings-btn"
                      :disabled="savingSettings"
                      @click="saveSettings"
                    >
                      <b-spinner v-if="savingSettings" small class="mr-2"></b-spinner>
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

    <!-- Post Modal -->
    <b-modal
      v-model="showSocialPostModal"
      :title="socialPostModalTitle"
    >
      <div v-if="postingItem">
        <p class="mb-3">{{ socialPostConfirmMessage(postingType) }}</p>
        <div><strong>Review Text: </strong>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span v-html="postingItem.text"></span>
        </div>
        <div v-if="postingType === 'instagram' && postingItem.instagram_post" class="mt-3">
          Posted to Instagram on {{ postingItem.instagram_post.front_date }}<br />
          Link: <a :href="'https://www.instagram.com/p/' + postingItem.instagram_post.ig_shortcode"
            target="_blank">https://www.instagram.com/p/{{ postingItem.instagram_post.ig_shortcode }}</a>
        </div>
        <div v-if="postingType === 'facebook' && postingItem.facebook_post" class="mt-3">
          Posted to Facebook on {{ postingItem.facebook_post.front_date }}<br />
          Link: <a :href="'https://www.facebook.com/' + postingItem.facebook_post.post_id"
            target="_blank">https://www.facebook.com/{{ postingItem.facebook_post.post_id }}</a>
        </div>
      </div>
      <template v-slot:modal-footer>
        <b-button variant="danger" @click="showSocialPostModal = false">Cancel</b-button>
        <b-button variant="success" @click="postSocial(postingItem, postingType)">
          <b-spinner v-if="postingItem && postingItem.working" small></b-spinner>
          Post
        </b-button>
      </template>
    </b-modal>

    <!-- Generate Post Modal -->
    <div id="review_post_modal" class="modal_container review_post_modal_wrapper fade">
      <div class="review_post_modal">
        <span id="modal_close" class="modal_close_icon" @click="fadeOut">&times;</span>
        <div class="modal_content_wrapper">
          <div class="modal_content">
            <div class="post_content">
              <div class="post">
                <div v-if="loading" class="loading_div">
                  <h2 class="generate">Generating Post!</h2>
                  <b-spinner class="spin" large></b-spinner>
                </div>
                <div v-else>
                  <div class="post_text_wrapper">
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <p id="post_text" class="post_text editable_text" dir="ltr" contenteditable @input="updatePostText" v-html="newPost.text"></p>
                    <br>
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <p id="post_tags" class="post_tags editable_text" dir="ltr" contenteditable @input="updatePostHashtags" v-html="newPost.hashtags"></p>
                  </div>
                  <div class="post_image_wrapper">
                    <img id="post_image" :src="newPost.imageUrl" alt="" class="post_image" />
                  </div>
                </div>
              </div>
              <button v-if="!loading" id="edit_post" class="edit_post_button" @click="editPost">Edit</button>
            </div>
            <div v-if="!loading" class="regenrate_buttons_container">
              <b-button variant="success" class="regenerate_btn" :disabled="loadingImage" @click="regenerate('image')">
                <b-spinner v-if="loadingImage" small></b-spinner>
                <span>Regenerate Image</span>
              </b-button>
              <b-button variant="success" class="regenerate_btn" :disabled="loadingPost" @click="regenerate('post')">
                <b-spinner v-if="loadingPost" small></b-spinner>
                <span>Regenerate Post</span>
              </b-button>
            </div>
            <div v-if="!loading" class="social_media_buttons_container">
              <b-button variant="success" class="social_post_btn" :disabled="loadingFacebook" @click="postSocial(null, 'facebook')">
                <b-spinner v-if="loadingFacebook" small></b-spinner>
                <span>Post To Facebook</span>
              </b-button>
              <b-button variant="success" class="social_post_btn" :disabled="loadingInstagram" @click="postSocial(null, 'instagram')">
                <b-spinner v-if="loadingInstagram" small></b-spinner>
                <span>Post To Instagram</span>
              </b-button>
              <b-button variant="success" class="social_post_btn" :disabled="loadingFbIg" @click="postSocial(null, 'instagram', true)">
                <b-spinner v-if="loadingFbIg" small></b-spinner>
                <span>Post To FB & IG</span>
              </b-button>
              <b-button variant="success" class="social_post_btn" :disabled="loadingGbp" @click="postSocial(null, 'google', true)">
                <b-spinner v-if="loadingGbp" small></b-spinner>
                <span>Post To Google</span>
              </b-button>
            </div>
          </div>
        </div>
        <button v-if="!loading" id="save_btn" class="save_btn" @click="saveNewPost">Save</button>
      </div>
    </div>

    <!-- Connect Alert Modal -->
    <b-modal v-model="showConnectAlert" content-class="keymodal">
      <p><strong>Important:</strong> Connect Your Social Media Accounts</p>
      <p>To start automating your social media content, please connect your social media accounts.</p>
      <p>Connecting your accounts ensures seamless content creation and scheduling, making your social media management effortless.</p>
      <b-button variant="primary" @click="gotoSettings">Connect Your Accounts</b-button>
    </b-modal>
  </div>
</template>

<script>
import Vue from 'vue'
import '../../../design/custom/components/_dashboard.scss'
import { businessLimitForUser } from '@utils/businessLimitsFeature'
import {
  socialPostModalTitle,
  socialPostConfirmMessage,
} from '@utils/socialPostModal'

export default {
  name: 'SocialPosts',
  data() {
    return {
      user: null,
      activeTab: 'posts',
      selectedCategory: 'Famous Quotes',
      additionalInfo: '',
      posts: [],
      postingItem: null,
      postingType: '',
      showSocialPostModal: false,
      socialPostModalTitle,
      loading: false,
      showModal: false,
      newPost: {
        text: '',
        imageUrl: '',
        hashtags: '',
        id: ''
      },
      loadedPostsCount: 2,
      hasMorePosts: true,
      loadingText: false,
      loadingImage: false,
      loadingPost: false,
      loadingFacebook: false,
      loadingInstagram: false,
      loadingGbp: false,
      loadingFbIg: false,
      showConnectAlert: false,
      overPostLimit: false,
      savingSettings: false,
      settings: {
        emailApproval: true,
        facebookAutoShare: false,
        instagramAutoShare: false,
        googleAutoShare: false,
        selectedPostTypes: ['Famous Quotes', 'Educational Content', 'Seasonal-Holiday Posts', 'Engagement Posts', 'Fun Facts', 'Humorous Posts', 'Quote of the Day', 'Interactive Content'],
        additionalInstructions: '',
        selectedDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        startTime: '9am',
        endTime: '3pm',
        postLimit: 1,
        timezone: 'America/New_York',
      },
      postTypes: ['Famous Quotes', 'Educational Content', 'Seasonal-Holiday Posts', 'Engagement Posts', 'Fun Facts', 'Humorous Posts', 'Quote of the Day', 'Interactive Content'],
      daysOfWeek: [
        { label: 'Monday', value: 'Monday' },
        { label: 'Tuesday', value: 'Tuesday' },
        { label: 'Wednesday', value: 'Wednesday' },
        { label: 'Thursday', value: 'Thursday' },
        { label: 'Friday', value: 'Friday' },
        { label: 'Saturday', value: 'Saturday' },
        { label: 'Sunday', value: 'Sunday' },
      ],
      timeOptions: ['12am', '1am', '2am', '3am', '4am', '5am', '6am', '7am', '8am', '9am', '10am', '11am', '12pm', '1pm', '2pm', '3pm', '4pm', '5pm', '6pm', '7pm', '8pm', '9pm', '10pm', '11pm'],
      limitOptions: [
        { value: 1, text: '1' },
        { value: 2, text: '2' },
        { value: 3, text: '3' },
        { value: 4, text: '4' },
        { value: 5, text: '5' },
      ],
      timezoneOptions: [
        { value: 'America/New_York', text: 'America/New_York' },
        { value: 'America/Chicago', text: 'America/Chicago' },
        { value: 'America/Denver', text: 'America/Denver' },
        { value: 'America/Los_Angeles', text: 'America/Los_Angeles' },
        { value: 'America/Phoenix', text: 'America/Phoenix' },
        { value: 'America/Anchorage', text: 'America/Anchorage' },
        { value: 'America/Honolulu', text: 'America/Honolulu' },
        { value: 'UTC', text: 'UTC' },
      ],
    };
  },

  computed: {
    formattedDate() {
      return (post) => {
        if (post && post.facebook_post && post.facebook_post.front_date) {
          const date = new Date(post.facebook_post.front_date);
          const options = {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric'
          };
          return date.toLocaleString('en-US', options);
        } else {
          return '';
        }
      };
    },
    formattedInstagramDate() {
      return (post) => {
        if (post && post.instagram_post && post.instagram_post.front_date) {
          const date = new Date(post.instagram_post.front_date);
          const options = {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric'
          };
          return date.toLocaleString('en-US', options);
        } else if (post && post.gbp_post && post.gbp_post.front_date) {
          const date = new Date(post.gbp_post.front_date);
          const options = {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric'
          };
          return date.toLocaleString('en-US', options);
        } else {
          return '';
        }
      };
    },
  },

  mounted() {
    this.user = Vue.util.extend({}, this.$store.getters['auth/user']);
    if (this.user && this.user.business) {
      this.fetchPosts();
      this.loadSettings();
    }
    this.loading = false;

    const token = this.$route.params.token;
    if (token) {
      const type = this.$route.params.type;
      const add = this.$route.params.add;

      this.selectedCategory = type;
      this.additionalInfo = add;
      this.generatePost();
    }

    if(this.user && this.user.google_accounts && this.user.google_accounts.length >= 1 && this.user.business && !this.user.business.facebook_token && this.user.business.is_social_fusion){
      setTimeout(() => {
        this.showConnectAlert = true;
      }, 250);
    }
  },

  methods: {
    socialPostConfirmMessage,
    gotoSettings() {
      window.location.href = "/business/settings";
    },
    editPost() {
      let elements = document.querySelectorAll('.editable_text');
      console.log(elements);
      elements.forEach(e => {
        if (e.getAttribute('contenteditable') === 'true') {
          e.setAttribute('contenteditable', 'false');
          e.style.border = 'none'
          e.style.padding = '0'
          e.style.whiteSpace = ''
          e.style.minHeight = ''
          e.style.resize = ''
          e.style.overflow = ''
          // Remove keydown event listener
          e.removeEventListener('keydown', this.handleEnterKey);
          document.getElementById('edit_post').innerHTML = "Edit";

        } else {
          e.setAttribute('contenteditable', 'true');
          e.style.border = '1px solid black'
          e.style.padding = '2px'
          e.style.whiteSpace = 'pre-wrap' // Allow multiline and preserve line breaks
          e.style.minHeight = '60px' // Minimum height for multiline editing
          e.style.resize = 'vertical' // Allow vertical resizing
          e.style.overflow = 'auto' // Show scrollbar if content overflows
          e.style.wordWrap = 'break-word' // Break long words
          e.style.overflowWrap = 'break-word' // Break words at appropriate points
          // Add keydown event listener for Enter key
          e.addEventListener('keydown', this.handleEnterKey);
          document.getElementById('edit_post').innerHTML = "Done";
        }
      });
      
      // When disabling edit mode, sync the content back to the model
      const isDisablingEdit = document.querySelector('.editable_text[contenteditable="true"]') === null;
      if (isDisablingEdit) {
        const postTextEl = document.getElementById('post_text');
        const postTagsEl = document.getElementById('post_tags');
        if (postTextEl) {
          this.newPost.text = postTextEl.innerHTML;
        }
        if (postTagsEl) {
          this.newPost.hashtags = postTagsEl.innerHTML;
        }
      }
    },
    handleEnterKey(event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        event.stopPropagation();
        
        // Use execCommand for reliable line break insertion
        // This is the standard way browsers handle line breaks in contenteditable
        const success = document.execCommand('insertLineBreak', false, null);
        
        if (!success) {
          // Fallback to manual insertion if execCommand fails
          const selection = window.getSelection();
          if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            range.deleteContents();
            
            // Insert line break
            const br = document.createElement('br');
            range.insertNode(br);
            
            // Ensure br is visible by adding a text node after it if at the end
            if (!br.nextSibling) {
              const textNode = document.createTextNode('');
              br.parentNode.insertBefore(textNode, br.nextSibling);
              range.setStart(textNode, 0);
              range.collapse(true);
            } else {
              range.setStartAfter(br);
              range.collapse(true);
            }
            
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
        
        // Don't update the model here - we'll sync when editing is done
        // This prevents Vue from re-rendering and resetting the cursor
        return false;
      }
    },
    updatePostText(event) {
      const el = event.target;
      // Don't update the model during editing to prevent Vue re-rendering
      // which causes cursor position to reset
      // The content is already in the DOM, we'll sync it when editing is done
      // Just store it temporarily without triggering reactivity
      if (el.getAttribute('contenteditable') === 'true') {
        // Only update if we're not in the middle of handling Enter key
        // The handleEnterKey will update the model
        return;
      }
      
      // Use innerHTML to preserve <br> tags and other formatting
      this.newPost.text = el.innerHTML;
    },
    updatePostHashtags(event) {
      const el = event.target;
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);

      // Save current selection
      const startOffset = range.startOffset;
      const endOffset = range.endOffset;

      this.newPost.hashtags = event.target.innerText;

      // Restore selection
      this.$nextTick(() => {
        this.restoreSelection(el, startOffset, endOffset);
      });
    },
    restoreSelection(el, startOffset, endOffset) {
      const range = document.createRange();
      const sel = window.getSelection();
      range.setStart(el.firstChild, startOffset);
      range.setEnd(el.firstChild, endOffset);
      sel.removeAllRanges();
      sel.addRange(range);
    },
    handleInput(el) {
      this.$nextTick(() => {
        this.placeCaretAtEnd(el);
      });
    },
    placeCaretAtEnd(el) {
      el.focus();
      if (typeof window.getSelection !== "undefined" && typeof document.createRange !== "undefined") {
        let range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        let sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      } else if (typeof document.body.createTextRange !== "undefined") {
        let textRange = document.body.createTextRange();
        textRange.moveToElementText(el);
        textRange.collapse(false);
        textRange.select();
      }else{
        console.error("neither if nor else")
      }
    },
    fadeOut() {
      let duration = 250;
      let element = document.getElementById('review_post_modal');

      element.style.transition = `opacity ${duration}ms`;
      element.style.opacity = 0;

      setTimeout(() => {
        element.style.display = 'none';
        element.classList.remove('show');
      }, duration);
    },
    loadMore() {
      this.loadedPostsCount += 2;
      this.fetchPosts();
    },
    confirmPostSocial(post, type) {
      this.postingItem = post;
      this.postingType = type;
      this.showSocialPostModal = true;
    },

    regenerate(type) {
      if (!this.user || !this.user.business) {
        return;
      }
      if (type === 'text') this.loadingText = true;
      if (type === 'image') this.loadingImage = true;
      if (type === 'post') this.loadingPost = true;

      let action = '';
      const prompt = this.generatePrompt();

      if (type === 'text') {
        action = 'posts/regenerateText';
      } else if (type === 'image') {
        action = 'posts/regenerateImage';
      } else if (type === 'post') {
        action = 'posts/regeneratePost';
      }

      this.$store.dispatch(action, {
        prompt: prompt,
        business_id: this.user.business.id,
        text: this.newPost.text,
        additional_info: this.additionalInfo,
      })
        .then(response => {
          if (response) {
            if (type === 'text' || type === 'post') {
              this.newPost.text = response.text;
              this.newPost.hashtags = response.hashtags;
            }
            if (type === 'image' || type === 'post') {
              this.newPost.imageUrl = response.imageUrl;
            }
          }
        })
        .catch(error => {
          console.error('Error regenerating:', error);
          this.loading = false;
        })
        .finally(() => {
          if (type === 'text') this.loadingText = false;
          if (type === 'image') this.loadingImage = false;
          if (type === 'post') this.loadingPost = false;
        });
    },

    postSocial(post, type, both = false) {
      if (!this.user || !this.user.business) {
        return;
      }
      if (type === 'facebook') this.loadingFacebook = true;
      if (type === 'instagram' && !both) this.loadingInstagram = true;
      if(type === 'google') this.loadingGbp = true;
      if (both) this.loadingFbIg = true;

      post = post || this.newPost;
      this.$set(post, 'working', true);

      const postToSocialMedia = (type) => {
        return this.$store.dispatch('posts/postSocial', {
          type: type,
          post_id: post.id,
          user_id: this.user.id,
          business_id: this.user.business.id
        });
      };

      this.saveNewPost(false)
        .then(() => {
          post = post || this.newPost;
          if (both) {
            // First post to Instagram, then to Facebook
            postToSocialMedia('instagram')
              .then((res) => {
                this.$set(post, 'instagram_post', res.instagram_post);
                return postToSocialMedia('facebook');
              })
              .then((res) => {
                this.$set(post, 'facebook_post', res.facebook_post);
                this.$set(post, 'working', false);
                this.fadeOut();
                this.fetchPosts();
                alert('Post has been successfully posted to Instagram and Facebook!');
                this.showSocialPostModal = false;
              })
              .catch((res) => {
                this.$set(post, 'working', false);
                console.log(res);
              })
              .finally(() => {
                this.loadingFbIg = false;
                this.showSocialPostModal = false;
              });
          } else {
            // Post to the specified type
            postToSocialMedia(type)
              .then((res) => {
                if (type === 'facebook') {
                  this.$set(post, 'facebook_post', res.facebook_post);
                } else if (type === 'instagram') {
                  this.$set(post, 'instagram_post', res.instagram_post);
                } else if (type === 'google') {
                  this.$set(post, 'gbp_post', res.gbp_post);
                }
                this.$set(post, 'working', false);
                this.fadeOut();
                this.fetchPosts();
                alert(`Post has been successfully posted to ${type.charAt(0).toUpperCase() + type.slice(1)}!`);
                this.showSocialPostModal = false;
              })
              .catch((res) => {
                this.$set(post, 'working', false);
                console.log(res);
              })
              .finally(() => {
                if (type === 'facebook') this.loadingFacebook = false;
                if (type === 'instagram' && !both) this.loadingInstagram = false;
                if(type === 'google') this.loadingGbp = false;
                this.showSocialPostModal = false;
              });
          }
        })
        .catch((err) => {
          this.$set(post, 'working', false);
          console.log(err);
        });
    },

    generatePost() {
      if (!this.user || !this.user.business) {
        return;
      }
      if(this.overPostLimit){
        Vue.$toast.open({message: "Sorry, but you have reached your monthly post limit", type: 'error', duration: 5000, position: 'bottom'});
        return
      }
      const prompt = this.generatePrompt();
      this.loading = true;
      let element = document.getElementById('review_post_modal');

      element.style.opacity = 0;
      element.style.display = 'grid';
      element.style.alignItems = 'center';
      element.style.transition = `opacity 250ms`;

      requestAnimationFrame(() => {
        element.style.opacity = 1;
      });

      setTimeout(() => {
        element.classList.add('show');
      }, 250);

      this.$store
        .dispatch('posts/generatePost', {
          prompt: prompt,
          additional_info: this.additionalInfo,
          user_id: this.user.id,
          business_id: this.user.business.id,
        })
        .then((res) => {
          if (res) {
            const reply = res;

            this.newPost = {
              text: reply.text,
              hashtags: reply.hashtags,
              imageUrl: reply.imageUrl,
              date: new Date().toLocaleString(),
            };

            this.updateModalContent();
          }
        })
        .catch((err) => {
          Vue.$toast.open({message: err.response.data.error, type: 'error', duration: 5000, position: 'bottom'});
        })
        .finally(() => {
          this.loading = false;
        });
    },

    updateModalContent() {
      // This method updates the modal content with the generated post
      let postTags = document.getElementById('post_tags');
      let postImage = document.getElementById('post_image');
      let postText = document.getElementById('post_text');

      postTags.innerHTML = this.newPost.hashtags;
      postText.innerHTML = this.newPost.text;
      postImage.src = this.newPost.imageUrl;
    },

    saveNewPost(fade=true) {
      return new Promise((resolve, reject) => {
        if (!this.user || !this.user.business) {
          resolve('done');
          return;
        }
        if (this.newPost.text !== "" && this.newPost.text) {
          this.$store
            .dispatch('posts/savePost', {
              user_id: this.user.id,
              business_id: this.user.business.id,
              hashtags: this.newPost.hashtags,
              text: this.newPost.text,
              image_url: this.newPost.imageUrl,
            })
            .then((res) => {
              this.newPost.id = res.id;
              if (!this.posts.some(post => post.id === this.newPost.id)) {
                  this.posts.unshift(this.newPost);
              }
              resolve(res);
              if(fade){
                this.fadeOut();
                alert('Post has been successfully saved.');
              }
            })
            .catch((err) => {
              console.log(err);
              reject(err);
            });
        } else {
          resolve('done');
        }
      });
    },

    generatePrompt() {
      const date = new Date();
      const today = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
      const Holidays = {
        "2025-01-01": "New Year's Day",
        "2025-01-02": "Science Fiction Day",
        "2025-01-03": "Festival of Sleep Day",
        "2025-01-04": "World Braille Day",
        "2025-01-05": "National Bird Day",
        "2025-01-06": "Epiphany",
        "2025-01-07": "Orthodox Christmas Day",
        "2025-01-08": "Bubble Bath Day",
        "2025-01-09": "Law Enforcement Appreciation Day",
        "2025-01-10": "Houseplant Appreciation Day",
        "2025-01-11": "Learn Your Name in Morse Code Day",
        "2025-01-12": "National Pharmacist Day",
        "2025-01-13": "Make Your Dream Come True Day",
        "2025-01-14": "Dress Up Your Pet Day",
        "2025-01-15": "National Hat Day",
        "2025-01-16": "Nothing Day",
        "2025-01-17": "Ditch New Year's Resolution Day",
        "2025-01-18": "Winnie the Pooh Day",
        "2025-01-19": "Popcorn Day",
        "2025-01-20": "Martin Luther King Jr. Day",
        "2025-01-21": "National Hugging Day",
        "2025-01-22": "Celebration of Life Day",
        "2025-01-23": "Handwriting Day",
        "2025-01-24": "Compliment Day",
        "2025-01-25": "Opposite Day",
        "2025-01-26": "Australia Day",
        "2025-01-27": "Chocolate Cake Day",
        "2025-01-28": "Data Privacy Day",
        "2025-01-29": "Puzzle Day",
        "2025-01-30": "Croissant Day",
        "2025-01-31": "Backward Day",
        "2025-02-01": "National Freedom Day",
        "2025-02-02": "Groundhog Day",
        "2025-02-03": "Golden Retriever Day",
        "2025-02-04": "World Cancer Day",
        "2025-02-05": "National Weatherperson's Day",
        "2025-02-06": "Frozen Yogurt Day",
        "2025-02-07": "Send a Card to a Friend Day",
        "2025-02-08": "Laugh and Get Rich Day",
        "2025-02-09": "Pizza Day",
        "2025-02-10": "Umbrella Day",
        "2025-02-11": "Inventors' Day",
        "2025-02-12": "Darwin Day",
        "2025-02-13": "Galentine's Day",
        "2025-02-14": "Valentine's Day",
        "2025-02-15": "Susan B. Anthony Day",
        "2025-02-16": "Do a Grouch a Favor Day",
        "2025-02-17": "Random Acts of Kindness Day",
        "2025-02-18": "Drink Wine Day",
        "2025-02-19": "Chocolate Mint Day",
        "2025-02-20": "Love Your Pet Day",
        "2025-02-21": "International Mother Language Day",
        "2025-02-22": "Margarita Day",
        "2025-02-23": "Banana Bread Day",
        "2025-02-24": "Tortilla Chip Day",
        "2025-02-25": "Clam Chowder Day",
        "2025-02-26": "Tell a Fairy Tale Day",
        "2025-02-27": "Strawberry Day",
        "2025-02-28": "Public Sleeping Day",
        "2025-03-01": "World Compliment Day",
        "2025-03-02": "Old Stuff Day",
        "2025-03-03": "World Wildlife Day",
        "2025-03-04": "Grammar Day",
        "2025-03-05": "Cheese Doodle Day",
        "2025-03-06": "Dentist's Day",
        "2025-03-07": "Cereal Day",
        "2025-03-08": "International Women's Day",
        "2025-03-09": "Panic Day",
        "2025-03-10": "Mario Day",
        "2025-03-11": "Worship of Tools Day",
        "2025-03-12": "Plant a Flower Day",
        "2025-03-13": "Smart & Sexy Day",
        "2025-03-14": "Pi Day",
        "2025-03-15": "Ides of March",
        "2025-03-16": "Everything You Do is Right Day",
        "2025-03-17": "St. Patrick's Day",
        "2025-03-18": "Awkward Moments Day",
        "2025-03-19": "Let's Laugh Day",
        "2025-03-20": "International Day of Happiness",
        "2025-03-21": "World Poetry Day",
        "2025-03-22": "Goof Off Day",
        "2025-03-23": "Puppy Day",
        "2025-03-24": "Chocolate Covered Raisins Day",
        "2025-03-25": "Tolkien Reading Day",
        "2025-03-26": "Spinach Day",
        "2025-03-27": "World Theatre Day",
        "2025-03-28": "Something on a Stick Day",
        "2025-03-29": "Smoke and Mirrors Day",
        "2025-03-30": "Doctors' Day",
        "2025-03-31": "Crayon Day",
        "2025-04-01": "April Fools' Day",
        "2025-04-02": "Peanut Butter and Jelly Day",
        "2025-04-03": "Find a Rainbow Day",
        "2025-04-04": "Walk Around Things Day",
        "2025-04-05": "Deep Dish Pizza Day",
        "2025-04-06": "Carbonara Day",
        "2025-04-07": "World Health Day",
        "2025-04-08": "Zoo Lovers Day",
        "2025-04-09": "Winston Churchill Day",
        "2025-04-10": "Siblings Day",
        "2025-04-11": "Pet Day",
        "2025-04-12": "Grilled Cheese Sandwich Day",
        "2025-04-13": "Scrabble Day",
        "2025-04-14": "Look Up at the Sky Day",
        "2025-04-15": "Tax Day (USA)",
        "2025-04-16": "Wear Pajamas to Work Day",
        "2025-04-17": "Haiku Poetry Day",
        "2025-04-18": "Columnists Day",
        "2025-04-19": "Garlic Day",
        "2025-04-20": "Volunteer Recognition Day",
        "2025-04-21": "Kindergarten Day",
        "2025-04-22": "Earth Day",
        "2025-04-23": "World Book Day",
        "2025-04-24": "Pig in a Blanket Day",
        "2025-04-25": "DNA Day",
        "2025-04-26": "Pretzel Day",
        "2025-04-27": "Tell a Story Day",
        "2025-04-28": "Superhero Day",
        "2025-04-29": "Dance Day",
        "2025-04-30": "Honesty Day",
        "2025-05-01": "May Day",
        "2025-05-02": "World Tuna Day",
        "2025-05-03": "Lemonade Day",
        "2025-05-04": "Star Wars Day",
        "2025-05-05": "Cinco de Mayo",
        "2025-05-06": "Nurses Day",
        "2025-05-07": "No Pants Day",
        "2025-05-08": "World Red Cross Day",
        "2025-05-09": "Lost Sock Memorial Day",
        "2025-05-10": "Clean Up Your Room Day",
        "2025-05-11": "Twilight Zone Day",
        "2025-05-12": "Limerick Day",
        "2025-05-13": "Apple Pie Day",
        "2025-05-14": "Dance Like a Chicken Day",
        "2025-05-15": "Chocolate Chip Day",
        "2025-05-16": "Love a Tree Day",
        "2025-05-17": "Pack Rat Day",
        "2025-05-18": "No Dirty Dishes Day",
        "2025-05-19": "Devil's Food Cake Day",
        "2025-05-20": "Be a Millionaire Day",
        "2025-05-21": "Talk Like Yoda Day",
        "2025-05-22": "Sherlock Holmes Day",
        "2025-05-23": "Taffy Day",
        "2025-05-24": "Scavenger Hunt Day",
        "2025-05-25": "Towel Day",
        "2025-05-26": "Blueberry Cheesecake Day",
        "2025-05-27": "Sunscreen Day",
        "2025-05-28": "Hamburger Day",
        "2025-05-29": "Paperclip Day",
        "2025-05-30": "Mint Julep Day",
        "2025-05-31": "Macaroon Day",
        "2025-06-01": "Say Something Nice Day",
        "2025-06-02": "Rocky Road Day",
        "2025-06-03": "Repeat Day",
        "2025-06-04": "Cheese Day",
        "2025-06-05": "World Environment Day",
        "2025-06-06": "D-Day Anniversary",
        "2025-06-07": "Chocolate Ice Cream Day",
        "2025-06-08": "Best Friends Day",
        "2025-06-09": "Donald Duck Day",
        "2025-06-10": "Iced Tea Day",
        "2025-06-11": "Corn on the Cob Day",
        "2025-06-12": "Loving Day",
        "2025-06-13": "Sewing Machine Day",
        "2025-06-14": "Flag Day (USA)",
        "2025-06-15": "Nature Photography Day",
        "2025-06-16": "Fresh Veggies Day",
        "2025-06-17": "Eat Your Vegetables Day",
        "2025-06-18": "Go Fishing Day",
        "2025-06-19": "Juneteenth",
        "2025-06-20": "Ice Cream Soda Day",
        "2025-06-21": "International Yoga Day",
        "2025-06-22": "Onion Rings Day",
        "2025-06-23": "Pink Day",
        "2025-06-24": "Fairy Day",
        "2025-06-25": "Catfish Day",
        "2025-06-26": "Chocolate Pudding Day",
        "2025-06-27": "Pineapple Day",
        "2025-06-28": "Paul Bunyan Day",
        "2025-06-29": "Camera Day",
        "2025-06-30": "Meteor Watch Day",
        "2025-07-01": "International Joke Day",
        "2025-07-02": "World UFO Day",
        "2025-07-03": "Stay Out of the Sun Day",
        "2025-07-04": "Independence Day (USA)",
        "2025-07-05": "Workaholics Day",
        "2025-07-06": "Fried Chicken Day",
        "2025-07-07": "World Chocolate Day",
        "2025-07-08": "Video Games Day",
        "2025-07-09": "Sugar Cookie Day",
        "2025-07-10": "Pina Colada Day",
        "2025-07-11": "Cheer Up the Lonely Day",
        "2025-07-12": "Paper Bag Day",
        "2025-07-13": "French Fries Day",
        "2025-07-14": "Mac & Cheese Day",
        "2025-07-15": "Gummi Worm Day",
        "2025-07-16": "Fresh Spinach Day",
        "2025-07-17": "World Emoji Day",
        "2025-07-18": "Caviar Day",
        "2025-07-19": "Daiquiri Day",
        "2025-07-20": "Moon Landing Day",
        "2025-07-21": "Junk Food Day",
        "2025-07-22": "Hammock Day",
        "2025-07-23": "Vanilla Ice Cream Day",
        "2025-07-24": "Cousins Day",
        "2025-07-25": "Wine and Cheese Day",
        "2025-07-26": "Aunt and Uncle Day",
        "2025-07-27": "Scotch Whisky Day",
        "2025-07-28": "Milk Chocolate Day",
        "2025-07-29": "Lasagna Day",
        "2025-07-30": "International Friendship Day",
        "2025-07-31": "Mutt Day",
        "2025-08-01": "Spider-Man Day",
        "2025-08-02": "Ice Cream Sandwich Day",
        "2025-08-03": "Watermelon Day",
        "2025-08-04": "Chocolate Chip Cookie Day",
        "2025-08-05": "Work Like a Dog Day",
        "2025-08-06": "Root Beer Float Day",
        "2025-08-07": "Purple Heart Day",
        "2025-08-08": "International Cat Day",
        "2025-08-09": "Book Lovers Day",
        "2025-08-10": "S'mores Day",
        "2025-08-11": "Mountain Day (Japan)",
        "2025-08-12": "Middle Child Day",
        "2025-08-13": "Left-Handers Day",
        "2025-08-14": "Creamsicle Day",
        "2025-08-15": "Relaxation Day",
        "2025-08-16": "Tell a Joke Day",
        "2025-08-17": "Black Cat Appreciation Day",
        "2025-08-18": "Serendipity Day",
        "2025-08-19": "World Photography Day",
        "2025-08-20": "Lemonade Day",
        "2025-08-21": "Senior Citizens Day",
        "2025-08-22": "Be an Angel Day",
        "2025-08-23": "Ride the Wind Day",
        "2025-08-24": "Pluto Demoted Day",
        "2025-08-25": "Banana Split Day",
        "2025-08-26": "Dog Day",
        "2025-08-27": "Just Because Day",
        "2025-08-28": "Bow Tie Day",
        "2025-08-29": "More Herbs, Less Salt Day",
        "2025-08-30": "Beach Day",
        "2025-08-31": "Trail Mix Day",
        "2025-09-01": "Labor Day (USA)",
        "2025-09-02": "Blueberry Popsicle Day",
        "2025-09-03": "Skyscraper Day",
        "2025-09-04": "Wildlife Day",
        "2025-09-05": "Cheese Pizza Day",
        "2025-09-06": "Read a Book Day",
        "2025-09-07": "Grandparents Day",
        "2025-09-08": "Literacy Day",
        "2025-09-09": "Teddy Bear Day",
        "2025-09-10": "Swap Ideas Day",
        "2025-09-11": "Patriot Day",
        "2025-09-12": "Chocolate Milkshake Day",
        "2025-09-13": "Fortune Cookie Day",
        "2025-09-14": "Cream-Filled Doughnut Day",
        "2025-09-15": "Make a Hat Day",
        "2025-09-16": "Guacamole Day",
        "2025-09-17": "Apple Dumpling Day",
        "2025-09-18": "Cheeseburger Day",
        "2025-09-19": "Talk Like a Pirate Day",
        "2025-09-20": "Punch Day",
        "2025-09-21": "World Gratitude Day",
        "2025-09-22": "Hobbit Day",
        "2025-09-23": "Checkers Day",
        "2025-09-24": "Punctuation Day",
        "2025-09-25": "Comic Book Day",
        "2025-09-26": "Love Note Day",
        "2025-09-27": "Crush a Can Day",
        "2025-09-28": "Good Neighbor Day",
        "2025-09-29": "Coffee Day",
        "2025-09-30": "Hot Mulled Cider Day",
        "2025-10-01": "World Vegetarian Day",
        "2025-10-02": "Name Your Car Day",
        "2025-10-03": "Techies Day",
        "2025-10-04": "Taco Day",
        "2025-10-05": "Do Something Nice Day",
        "2025-10-06": "Mad Hatter Day",
        "2025-10-07": "Frappé Day",
        "2025-10-08": "Pierogi Day",
        "2025-10-09": "Beer and Pizza Day",
        "2025-10-10": "World Mental Health Day",
        "2025-10-11": "Sausage Pizza Day",
        "2025-10-12": "Gumbo Day",
        "2025-10-13": "Train Your Brain Day",
        "2025-10-14": "Dessert Day",
        "2025-10-15": "Grouch Day",
        "2025-10-16": "Boss's Day",
        "2025-10-17": "Pasta Day",
        "2025-10-18": "Chocolate Cupcake Day",
        "2025-10-19": "Evaluate Your Life Day",
        "2025-10-20": "Sloth Day",
        "2025-10-21": "Reptile Awareness Day",
        "2025-10-22": "Nut Day",
        "2025-10-23": "Mole Day",
        "2025-10-24": "Food Day",
        "2025-10-25": "Punk for a Day Day",
        "2025-10-26": "Pumpkin Day",
        "2025-10-27": "Black Cat Day",
        "2025-10-28": "Chocolate Day",
        "2025-10-29": "Internet Day",
        "2025-10-30": "Candy Corn Day",
        "2025-10-31": "Halloween",
        "2025-11-01": "Authors' Day",
        "2025-11-02": "Deviled Egg Day",
        "2025-11-03": "Sandwich Day",
        "2025-11-04": "Candy Day",
        "2025-11-05": "Donut Day",
        "2025-11-06": "Saxophone Day",
        "2025-11-07": "Bittersweet Chocolate Day",
        "2025-11-08": "Cappuccino Day",
        "2025-11-09": "Go to an Art Museum Day",
        "2025-11-10": "Vanilla Cupcake Day",
        "2025-11-11": "Veterans Day",
        "2025-11-12": "Chicken Soup for the Soul Day",
        "2025-11-13": "Kindness Day",
        "2025-11-14": "Pickle Day",
        "2025-11-15": "Clean Out Your Refrigerator Day",
        "2025-11-16": "Fast Food Day",
        "2025-11-17": "Baklava Day",
        "2025-11-18": "Mickey Mouse Day",
        "2025-11-19": "Carbonated Beverage Day",
        "2025-11-20": "Peanut Butter Fudge Day",
        "2025-11-21": "World Television Day",
        "2025-11-22": "Go for a Ride Day",
        "2025-11-23": "Espresso Day",
        "2025-11-24": "Sardines Day",
        "2025-11-25": "Shopping Reminder Day",
        "2025-11-26": "Cake Day",
        "2025-11-27": "Thanksgiving (USA)",
        "2025-11-28": "Black Friday",
        "2025-11-29": "Electronic Greetings Day",
        "2025-11-30": "Mousse Day",
        "2025-12-01": "World AIDS Day",
        "2025-12-02": "Fritters Day",
        "2025-12-03": "Make a Gift Day",
        "2025-12-04": "Cookie Day",
        "2025-12-05": "Ninja Day",
        "2025-12-06": "Microwave Oven Day",
        "2025-12-07": "Pearl Harbor Remembrance Day",
        "2025-12-08": "Brownie Day",
        "2025-12-09": "Pastry Day",
        "2025-12-10": "Human Rights Day",
        "2025-12-11": "Noodle Ring Day",
        "2025-12-12": "Gingerbread House Day",
        "2025-12-13": "Cocoa Day",
        "2025-12-14": "Monkey Day",
        "2025-12-15": "Cupcake Day",
        "2025-12-16": "Chocolate-Covered Anything Day",
        "2025-12-17": "Maple Syrup Day",
        "2025-12-18": "Bake Cookies Day",
        "2025-12-19": "Hard Candy Day",
        "2025-12-20": "Sangria Day",
        "2025-12-21": "Winter Solstice",
        "2025-12-22": "Date Nut Bread Day",
        "2025-12-23": "Festivus",
        "2025-12-24": "Christmas Eve",
        "2025-12-25": "Christmas Day",
        "2025-12-26": "Kwanzaa Begins",
        "2025-12-27": "Fruitcake Day",
        "2025-12-28": "Card Playing Day",
        "2025-12-29": "Pepper Pot Day",
        "2025-12-30": "Bacon Day",
        "2025-12-31": "New Year's Eve"
      }
      const todayHoliday = Holidays[today];
      const categoryPrompts = {
        'Famous Quotes': 'Generate a unique, lesser-known famous quote relevant to business or life. Avoid widely used quotes and do not use unknown or anonymous authors — only well-known individuals. Focus on themes like leadership, innovation, perseverance, teamwork, or success. Format as: "Quote" – Author. No additional text.',
        'Educational Content': 'Create a concise, informative piece of educational content that delivers value. Choose a topic from themes like history, science, technology, health, or personal development. Ensure it covers a different topic than previous content.',
        'Seasonal-Holiday Posts': `Today's event is ${todayHoliday}. Write a short, thoughtful message that reflects the meaning of ${todayHoliday}. It may be celebratory, reflective, or nostalgic, but keep it sincere and relevant. End with: #${todayHoliday} #Reflection`,
        'Engagement Posts': 'Write a single, open-ended question to spark audience interaction. The topic should be fresh and fall under themes like daily habits, personal goals, favorite hobbies, memorable experiences, or current events.',
        'Fun Facts': 'Share one unique and interesting fun fact. Choose a topic from themes like animals, history, space, the human body, or technology. Avoid common facts and duplication.',
        'Humorous Posts': 'Write one short and funny piece of content — such as a witty one-liner, light joke, or meme-style caption. Focus on themes like workplace humor, daily life, technology, relationships, or puns. Avoid repetition and crude humor.',
        'Quote of the Day': 'Generate one motivational or thought-provoking quote. Use themes like inspiration, wisdom, courage, creativity, or resilience. The quote must be credited to a known author and formatted as: "Quote" – Author. No extra text.',
        'Interactive Content': 'Write a single interactive content idea such as a poll, challenge, trivia question, or mini-survey. Focus on themes like preferences, knowledge, pop culture, or fun participation. Keep it short and engaging.'
      };

      return categoryPrompts[this.selectedCategory];
    },

    fetchPosts() {
      if (!this.user || !this.user.business) {
        return;
      }
      this.$store
        .dispatch('posts/getPosts', {
          user_id: this.user.id,
          business_id: this.user.business.id,
          limit: this.loadedPostsCount
        })
        .then((res) => {
          if (res.data.length < this.loadedPostsCount) {
            this.hasMorePosts = false;
          }
          if (typeof res.can_post === 'boolean') {
            this.overPostLimit = !res.can_post;
          } else {
            const monthlyLimitRaw = this?.user?.business?.limits?.social_fusion_post_limit;
            const monthlyLimit = Number.isFinite(Number(monthlyLimitRaw)) ? Number(monthlyLimitRaw) : 0;
            this.overPostLimit = monthlyLimit > 0 && res.posts_this_month >= monthlyLimit;
          }
          this.posts = res.data.map(post => {
            if(post.image_url)
              post.imageUrl = post.image_url.replace(/\\\//g, '/');
            return post;
          });
        })
        .catch((error) => {
          console.log(error);
        });
    },
    loadSettings() {
      if (!this.user || !this.user.business) {
        return;
      }
      // Load settings from user.business.social_fusion_settings if available
      if (this.user.business.social_fusion_settings && this.user.business.social_fusion_settings.auto_sharing_settings) {
        const autoSettings = this.user.business.social_fusion_settings.auto_sharing_settings;
        const toUiDay = (d) =>
          typeof d === 'string' && d.length ? d.charAt(0).toUpperCase() + d.slice(1).toLowerCase() : d;
        if (autoSettings.email_approval !== undefined) {
          this.settings.emailApproval = autoSettings.email_approval;
        }
        // API stores fb_enabled / ig_enabled / gbp_enabled (see BusinessResource & scheduler)
        if (autoSettings.fb_enabled !== undefined && autoSettings.fb_enabled !== null) {
          this.settings.facebookAutoShare = !!autoSettings.fb_enabled;
        } else if (autoSettings.facebook_auto_share !== undefined) {
          this.settings.facebookAutoShare = !!autoSettings.facebook_auto_share;
        }
        if (autoSettings.ig_enabled !== undefined && autoSettings.ig_enabled !== null) {
          this.settings.instagramAutoShare = !!autoSettings.ig_enabled;
        } else if (autoSettings.instagram_auto_share !== undefined) {
          this.settings.instagramAutoShare = !!autoSettings.instagram_auto_share;
        } else {
          this.settings.instagramAutoShare = false;
        }
        if (autoSettings.gbp_enabled !== undefined && autoSettings.gbp_enabled !== null) {
          this.settings.googleAutoShare = !!autoSettings.gbp_enabled;
        } else if (autoSettings.google_auto_share !== undefined) {
          this.settings.googleAutoShare = !!autoSettings.google_auto_share;
        }
        if (autoSettings.post_type) {
          this.settings.selectedPostTypes = Array.isArray(autoSettings.post_type) ? autoSettings.post_type : [];
        }
        if (autoSettings.additional_instructions) {
          this.settings.additionalInstructions = autoSettings.additional_instructions;
        }
        if (autoSettings.share_days) {
          // Scheduler uses Carbon dayName e.g. "Monday"; day buttons use lowercase values
          this.settings.selectedDays = Array.isArray(autoSettings.share_days)
            ? autoSettings.share_days.map((d) => toUiDay(d).toLowerCase())
            : [];
        }
        if (autoSettings.share_start_time) {
          this.settings.startTime = autoSettings.share_start_time;
        }
        if (autoSettings.share_end_time) {
          this.settings.endTime = autoSettings.share_end_time;
        }
        if (autoSettings.share_daily_limit) {
          this.settings.postLimit = autoSettings.share_daily_limit;
        }
        if (autoSettings.timezone) {
          this.settings.timezone = autoSettings.timezone;
        }
      }
    },
    togglePostType(postType) {
      const index = this.settings.selectedPostTypes.indexOf(postType);
      if (index > -1) {
        this.settings.selectedPostTypes.splice(index, 1);
      } else {
        this.settings.selectedPostTypes.push(postType);
      }
    },
    toggleDay(day) {
      const index = this.settings.selectedDays.indexOf(day);
      if (index > -1) {
        this.settings.selectedDays.splice(index, 1);
      } else {
        this.settings.selectedDays.push(day);
      }
    },
    saveSettings() {
      if (!this.user || !this.user.business) {
        return;
      }
      this.savingSettings = true;

      const storeUser = this.$store.getters['auth/user'];
      const fusion = JSON.parse(JSON.stringify(storeUser.business.social_fusion_settings || {}));
      const priorAuto = { ...(fusion.auto_sharing_settings || {}) };
      const capitalizeDay = (d) =>
        typeof d === 'string' && d.length ? d.charAt(0).toUpperCase() + d.slice(1).toLowerCase() : d;
      const shareDays = (this.settings.selectedDays || []).map(capitalizeDay);
      const postTypes =
        this.settings.selectedPostTypes && this.settings.selectedPostTypes.length
          ? this.settings.selectedPostTypes
          : priorAuto.post_type || ['Famous Quotes'];

      fusion.auto_sharing_settings = {
        ...priorAuto,
        fb_enabled: !!this.settings.facebookAutoShare,
        ig_enabled: !!this.settings.instagramAutoShare,
        gbp_enabled: !!this.settings.googleAutoShare,
        post_type: postTypes,
        additional_instructions: this.settings.additionalInstructions,
        share_days: shareDays,
        share_start_time: this.settings.startTime,
        share_end_time: this.settings.endTime,
        share_daily_limit: this.settings.postLimit,
        timezone: this.settings.timezone,
      };

      this.$store
        .dispatch('business/editSocialFusionSettings', {
          business_id: this.user.business.id,
          settings: fusion,
        })
        .then(() => {
          this.savingSettings = false;
          this.user = Vue.util.extend({}, this.$store.getters['auth/user']);
          this.loadSettings();
          Vue.$toast.open({
            message: 'Settings saved successfully',
            type: 'success',
            duration: 3000,
            position: 'bottom'
          });
        })
        .catch((error) => {
          this.savingSettings = false;
          Vue.$toast.open({
            message: 'Failed to save settings. Please try again.',
            type: 'error',
            duration: 5000,
            position: 'bottom'
          });
          console.error(error);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.social-posts-page {
  padding: 24px 28px 40px;
  min-height: 100vh;
  background: #f3f4f6;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.page-divider {
  margin: 20px 0 24px;
  border: 0;
  border-top: 1px solid #e5e7eb;
}

.header-section {
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  padding: 8px 32px;
  margin-left: -24px;
  margin-right: -24px;
  margin-bottom: 24px;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.page-description {
  color: #6b7280;
  font-size: 14px;
  margin-top: 4px;
  margin-bottom: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.request-review-btn {
  background-color: rgb(36, 99, 235) !important;
  color: #ffffff !important;
}


.tabs-container {
  width: 100%;
  margin-bottom: 8px;
}

.tabs-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  width: 100%;
  height: 40px;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: #f3f4f6;
  padding: 4px;
}

.tab-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 4px;
  transition: all 0.2s;
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;

  &:hover {
    color: #1f2937;
  }

  &.active {
    background: #fff;
    color: #1f2937;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
}

.tab-panels {
  margin-top: 24px;
}

.tab-panel {
  outline: none;
}

.space-y-8 {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

.card {
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.card-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 28px 28px 20px;
  background: #f9fafb !important;
  border-bottom: 1px solid #e5e7eb !important;
  color: #111827 !important;

  .card-title,
  .card-title-muted {
    color: #111827 !important;
  }

  .card-description {
    color: #4b5563 !important;
  }
}

.card-title {
  font-size: 24px;
  font-weight: 600;
  line-height: 1.2;
  color: #111827 !important;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  .title-icon {
    width: 20px;
    height: 20px;
    color: #374151 !important;
  }
}

.card-title-muted {
  font-size: 24px;
  font-weight: 600;
  line-height: 1.2;
  color: #111827 !important;
  margin: 0;
}

.card-description {
  font-size: 14px;
  color: #4b5563 !important;
  margin: 0;
}

.card-body {
  padding: 28px;
  padding-top: 8px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 2fr;
  }
}

.form-col-1,
.form-col-2 {
  display: flex;
  flex-direction: column;
}

.form-label {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 12px;
  display: block;
  color: #111827;
}

.post-type-select {
  height: 40px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  padding: 0 12px;
  font-size: 14px;
  color: #111827;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
}

.instructions-textarea {
  width: 100%;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  padding: 8px 12px;
  font-size: 14px;
  min-height: 100px;
  resize: none;
  color: #111827;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
}

.button-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
}

.generate-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
  border-radius: 6px;
  font-size: 14px;
  height: 40px;
  padding: 0 32px;
  background: #3b82f6;
  color: #fff;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: rgba(59, 130, 246, 0.9);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.empty-posts {
  padding: 48px 24px;
  text-align: center;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px dashed #d1d5db;
}

.empty-posts-text {
  font-size: 14px;
  color: #4b5563;
  margin: 0;
}

.posts-list {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.post-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 28px;
  background: #f9fafb;
}

.post-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 28px;

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 2fr;
  }
}

.post-image-col {
  width: 100%;
}

.post-image {
  width: 100%;
  height: 192px;
  object-fit: cover;
  border-radius: 8px;
  background: #f3f4f6;
}

.post-content-col {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.post-text-content {
  .post-text {
    font-size: 18px;
    font-weight: 500;
    color: #111827;
    margin-bottom: 8px;
    margin-top: 0;
  }

  .post-hashtags {
    font-size: 14px;
    color: #2563eb;
    margin: 0;
  }
}

.post-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.post-social-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 500;
  height: 36px;
  border-radius: 6px;
  padding: 0 12px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;

  .btn-icon {
    width: 16px;
    height: 16px;
  }

  &.btn-facebook {
    background: #1877F2;
    color: #fff;

    &:hover:not(:disabled) {
      background: rgba(24, 119, 242, 0.9);
    }

    &.btn-posted {
      background: #10b981;
    }
  }

  &.btn-instagram {
    background: linear-gradient(to right, #F58529, #DD2A7B, #8134AF);
    color: #fff;

    &:hover:not(:disabled) {
      opacity: 0.9;
    }

    &.btn-posted {
      background: #10b981;
    }
  }

  &.btn-google {
    background: #4285F4;
    color: #fff;

    &:hover:not(:disabled) {
      background: rgba(66, 133, 244, 0.9);
    }

    &.btn-posted {
      background: #10b981;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.post-dates {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.post-date {
  font-size: 12px;
  color: #ef4444;
  font-weight: 600;
}

.load-more-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}

.load-more-btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #1f2937;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
}

.settings-content {
  padding: 28px;
  color: #6b7280;
}

// Settings Styles
.settings-list {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.setting-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
}

.setting-label-group {
  flex: 1;
  margin-right: 16px;
}

.setting-label {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  display: block;
  margin-bottom: 4px;
}

.setting-hint {
  font-size: 12px;
  color: #6b7280;
  margin: 0;
}

.setting-label-with-icon {
  display: flex;
  align-items: center;
  gap: 8px;
}

.platform-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.feedback-switch-style {
  margin: 0;
  flex-shrink: 0;
  align-self: center;
  display: flex;
  align-items: center;

  :deep(.custom-control) {
    display: flex;
    align-items: center;
    padding-left: 0;
    min-height: auto;
  }

  :deep(.custom-control-label) {
    padding-left: 2.5rem;
    padding-top: 0;
    display: flex;
    align-items: center;
    min-height: 1.125rem;
  }

  :deep(.custom-control-input:checked ~ .custom-control-label::before) {
    background-color: #3b82f6;
    border-color: #3b82f6;
  }

  :deep(.custom-control-input:focus ~ .custom-control-label::before) {
    box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.25);
  }

  :deep(.custom-control-label::before) {
    width: 2rem;
    height: 1.125rem;
    border-radius: 0.5625rem;
    background-color: #d1d5db;
    border-color: #d1d5db;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
  }

  :deep(.custom-control-label::after) {
    width: calc(1.125rem - 4px);
    height: calc(1.125rem - 4px);
    border-radius: 50%;
    background-color: #fff;
    top: 50%;
    left: 2px;
    transform: translateY(-50%);
  }

  :deep(.custom-control-input:checked ~ .custom-control-label::after) {
    transform: translateY(-50%) translateX(0.875rem);
  }
}

.settings-field {
  margin-bottom: 36px;

  &:last-of-type {
    margin-bottom: 0;
  }
}

.field-label {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  display: block;
  margin-bottom: 12px;
}

.field-hint {
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 8px 0;
}

.post-types-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
}

.post-type-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 16px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #9ca3af;
    background: #f9fafb;
  }

  &.active {
    background: #3b82f6;
    border-color: #3b82f6;
    color: #fff;
  }

  .tag-remove {
    font-size: 18px;
    line-height: 1;
    margin-left: 4px;
  }
}

.settings-textarea {
  width: 100%;
  min-height: 100px;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #1f2937;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
}

.days-selection {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
  margin-bottom: 24px;
}

.day-button {
  padding: 8px 16px;
  border-radius: 16px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #9ca3af;
    background: #f9fafb;
  }

  &.active {
    background: #3b82f6;
    border-color: #3b82f6;
    color: #fff;
  }
}

.scheduling-controls-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 28px;

  @media (min-width: 1024px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 28px;
  }
}

.time-range-group {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;

  @media (min-width: 1024px) {
    margin-bottom: 0;
  }
}

.time-range-label {
  font-size: 14px;
  color: #1f2937;
}

.time-select {
  width: 100px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 14px;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
}

.limit-group {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;

  @media (min-width: 1024px) {
    margin-bottom: 0;
    flex: 1;
    justify-content: center;
  }
}

.limit-label {
  font-size: 14px;
  color: #1f2937;
}

.limit-select {
  width: 80px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 14px;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
}

.timezone-group {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;

  @media (min-width: 1024px) {
    margin-bottom: 0;
  }
}

.timezone-label {
  font-size: 14px;
  color: #1f2937;
}

.timezone-select {
  width: 200px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 14px;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
}

.settings-footer {
  display: flex;
  justify-content: flex-start;
  margin-top: 32px;
  padding-top: 28px;
  border-top: 1px solid #e5e7eb;
}

.save-settings-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  background: #1f2937;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #374151;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// Modal styles
.modal_container {
  min-width: 100vw;
  min-height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.1);
  display: grid;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.review_post_modal {
  background: #fff;
  height: 100%;
  max-height: 80vh;
  position: relative;
  overflow-y: scroll;
  padding: 1rem;
  border-radius: 8px;

  .modal_close_icon {
    position: absolute;
    top: 0.5rem;
    right: 0.8rem;
    font-weight: 800;
    font-size: 2rem;
    cursor: pointer;
    color: #6b7280;
    z-index: 10;

    &:hover {
      color: #1f2937;
    }
  }

  .modal_content_wrapper {
    padding: 2rem 4rem;

    @media (max-width: 768px) {
      padding: 2rem 1rem;
    }
  }

  .modal_content {
    .post_content {
      max-width: 20rem;
      margin-inline: auto;
      margin-bottom: 1rem;
      position: relative;

      @media (max-width: 768px) {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        align-items: flex-start;
        gap: 0.5rem;
      }

      .post_text_wrapper {
        background-color: #f2f2f2;
        padding: 0.5rem 0.8rem;
        margin-bottom: 5px;

        p {
          margin: 0;
        }
      }

      .post_image_wrapper {
        border: 1px solid #9ca3af;

        .post_image {
          width: 100%;
        }
      }

      .edit_post_button {
        border: none;
        background: #284374;
        color: #fff;
        padding: 0.3rem 1rem;
        border-radius: 4px;
        cursor: pointer;

        @media (min-width: 768px) {
          position: absolute;
          left: 102%;
          top: 0;
        }
      }
    }

    .regenrate_buttons_container {
      margin-bottom: 0.5rem;
      margin-inline: auto;
      text-align: center;
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 0.2rem;

      .regenerate_btn {
        margin-inline: 0.3rem;
        border: none;
        background: #06b050;
        color: #fff;
        padding: 0.3rem 1.5rem;
        border-radius: 4px;
        font-size: 12px;
        cursor: pointer;
      }
    }

    .social_media_buttons_container {
      margin-inline: auto;
      text-align: center;
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 0.2rem;

      .social_post_btn {
        margin-inline: 0.3rem;
        border: none;
        background: #284374;
        color: #fff;
        padding: 0.3rem 0.7rem;
        border-radius: 4px;
        font-size: 12px;
        cursor: pointer;
      }
    }
  }

  .save_btn {
    border: none;
    background: #284374;
    color: #fff;
    padding: 0.3rem 1rem;
    border-radius: 4px;
    float: right;
    cursor: pointer;
  }
}

.fade {
  transition: opacity 0.5s ease-in-out;
  opacity: 0;
  display: none;
}

.fade.show {
  opacity: 1;
}

.loading_div {
  height: 300px;
  width: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.generate {
  text-align: center;
  color: #1f2937;
  margin-bottom: 1rem;
}

.spin {
  height: 5rem;
  width: 5rem;
}
</style>

<style>
.keymodal .modal-body {
  text-align: center !important;
  font-size: 17px !important;
  color: black !important;
}

.keymodal .modal-footer {
  display: none !important;
}

.keymodal .btn {
  font-weight: bold;
  font-size: 17px;
}

@media (min-width: 768px) {
  #review_post_modal {
    padding-top: 4rem;
  }
}
</style>